import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import {
  algoService, AlgoSignal, AlgoSentiment, ClaudeAnalysis, AlgoTradePreview, SignalStatus,
  STRATEGIES, RISK_LABELS,
} from '../../services/algoService';
import '../Dashboard.css';

const ACTION_COLORS: Record<string, string> = {
  BUY: '#16a34a',
  SELL: '#dc2626',
  HOLD: '#6b7280',
};

const STATUS_COLORS: Record<string, string> = {
  pending:    '#d97706',
  submitted:  '#f59e0b',
  executed:   '#2563eb',
  dismissed:  '#4b5563',
};

function fmt(num: number | null | undefined, decimals = 2): string {
  if (num === null || num === undefined) return '—';
  return num.toFixed(decimals);
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const SENTIMENT_COLORS: Record<string, string> = {
  very_bullish: '#16a34a',
  bullish:      '#4ade80',
  neutral:      '#6b7280',
  bearish:      '#f97316',
  very_bearish: '#dc2626',
};

const SentimentBadge: React.FC<{ sentiment: AlgoSentiment }> = ({ sentiment }) => {
  const color = SENTIMENT_COLORS[sentiment.label] || '#6b7280';
  const label = sentiment.label.replace('_', ' ');
  return (
    <div style={{ background: '#111827', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: sentiment.catalyst || sentiment.headlines.length > 0 ? 8 : 0, flexWrap: 'wrap' }}>
        <span style={{ background: color + '25', color, borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>
          {label}
        </span>
        <span style={{ color: '#9ca3af', fontSize: 12 }}>
          Score: <strong style={{ color: '#e5e7eb' }}>{Math.round(sentiment.score * 100)}%</strong>
        </span>
        <span style={{ color: '#9ca3af', fontSize: 12 }}>
          Conf: <strong style={{ color: '#e5e7eb' }}>{Math.round(sentiment.confidence * 100)}%</strong>
        </span>
        <span style={{ color: '#6b7280', fontSize: 11 }}>{sentiment.sources_count} source{sentiment.sources_count !== 1 ? 's' : ''}</span>
      </div>
      {sentiment.catalyst && (
        <div style={{ color: '#d1d5db', fontSize: 12, marginBottom: sentiment.headlines.length > 0 ? 6 : 0 }}>
          {sentiment.catalyst}
        </div>
      )}
      {sentiment.headlines.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sentiment.headlines.slice(0, 2).map((h, i) => (
            <div key={i} style={{ color: '#6b7280', fontSize: 11 }}>
              <span style={{ color: '#9ca3af' }}>{h.publisher}: </span>{h.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RECOMMENDATION_COLORS: Record<string, string> = {
  EXECUTE: '#16a34a',
  HOLD: '#d97706',
  SKIP: '#dc2626',
};

const ClaudeAnalysisCard: React.FC<{ analysis: ClaudeAnalysis }> = ({ analysis }) => {
  const recColor = RECOMMENDATION_COLORS[analysis.recommendation] || '#6b7280';
  const confColor = analysis.confidence >= 80 ? '#16a34a' : analysis.confidence >= 60 ? '#d97706' : '#dc2626';

  return (
    <div style={{ background: '#0f172a', border: '1px solid #1e3a5f', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ color: '#818cf8', fontSize: 12, fontWeight: 700 }}>Claude AI Analysis</span>
        <span style={{ background: recColor + '25', color: recColor, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
          {analysis.recommendation}
        </span>
        <span style={{ color: '#9ca3af', fontSize: 12 }}>
          Confidence: <strong style={{ color: confColor }}>{analysis.confidence}%</strong>
        </span>
        {analysis.sizingAdjustment !== 1.0 && (
          <span style={{ color: '#9ca3af', fontSize: 11 }}>
            Size adj: {analysis.sizingAdjustment.toFixed(1)}x
          </span>
        )}
      </div>
      <div style={{ color: '#d1d5db', fontSize: 12, marginBottom: 8, lineHeight: 1.5 }}>
        {analysis.reasoning}
      </div>
      {analysis.keyRisks.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <span style={{ color: '#f87171', fontSize: 11, fontWeight: 600 }}>Risks: </span>
          <span style={{ color: '#9ca3af', fontSize: 11 }}>
            {analysis.keyRisks.join(' · ')}
          </span>
        </div>
      )}
      {analysis.marketContext && (
        <div style={{ color: '#6b7280', fontSize: 11, fontStyle: 'italic' }}>
          {analysis.marketContext}
        </div>
      )}
    </div>
  );
};

// ─── Execute Modal ─────────────────────────────────────────────────────────────

interface ModalState {
  signal: AlgoSignal;
  loading: boolean;
  error: string | null;
  preview: AlgoTradePreview | null;
  qty: number;
}

const ExecuteModal: React.FC<{
  state: ModalState;
  onQtyChange: (qty: number) => void;
  onConfirm: () => void;
  onClose: () => void;
  confirming: boolean;
}> = ({ state, onQtyChange, onConfirm, onClose, confirming }) => {
  const { signal, loading, error, preview, qty } = state;
  const tradeValue = preview ? qty * preview.entryPrice : 0;
  const pctOfEquity = preview && preview.equity > 0 ? (tradeValue / preview.equity) * 100 : 0;
  const overMax = preview ? qty > preview.maxQty : false;
  const overBp  = preview ? tradeValue > preview.buyingPower : false;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#1f2937', borderRadius: 12, padding: '28px 28px 24px',
        width: '100%', maxWidth: 420, boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#f9fafb' }}>
              Execute {signal.action} — {signal.symbol}
            </div>
            <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>
              Entry: ${fmt(signal.suggestedEntry)} &nbsp;·&nbsp; Stop: ${fmt(signal.suggestedStop)} &nbsp;·&nbsp; Target: ${fmt(signal.suggestedTarget)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>
            Fetching account info...
          </div>
        )}

        {error && !loading && (
          <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: 6, padding: '10px 12px', marginBottom: 16, fontSize: 13, whiteSpace: 'pre-line', lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        {preview && !loading && (
          <>
            {/* Account info */}
            <div style={{ background: '#111827', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 0' }}>
                <span style={{ color: '#6b7280', fontSize: 12 }}>Account Equity</span>
                <span style={{ color: '#f9fafb', fontSize: 13, fontWeight: 600, textAlign: 'right' }}>
                  ${preview.equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>Buying Power</span>
                <span style={{ color: '#93c5fd', fontSize: 13, fontWeight: 600, textAlign: 'right' }}>
                  ${preview.buyingPower.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Order type + bracket info */}
            <div style={{ background: '#111827', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: '#9ca3af' }}>Order Type</span>
                <span style={{ color: preview.orderType === 'bracket' ? '#a78bfa' : '#e5e7eb', fontWeight: 600 }}>
                  {preview.orderType === 'bracket' ? 'Bracket (Limit + Stop + Target)' : 'Limit'}
                </span>
              </div>
              {preview.orderType === 'bracket' && preview.suggestedStop && preview.suggestedTarget && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ color: '#9ca3af' }}>Stop Loss</span>
                    <span style={{ color: '#f87171', fontWeight: 600 }}>${fmt(preview.suggestedStop)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#9ca3af' }}>Take Profit</span>
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>${fmt(preview.suggestedTarget)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Suggestions */}
            <div style={{ background: '#111827', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: '#9ca3af' }}>Suggested ({RISK_LABELS[preview.riskAppetite]?.label} · {(preview.positionSizePct * 100).toFixed(0)}%)</span>
                <span style={{ color: '#e5e7eb', fontWeight: 600 }}>{preview.suggestedQty} shares</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#9ca3af' }}>Max per trade (10% cap)</span>
                <span style={{ color: '#e5e7eb', fontWeight: 600 }}>{preview.maxQty} shares</span>
              </div>
            </div>

            {/* Qty input */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#d1d5db', fontSize: 13, display: 'block', marginBottom: 6 }}>
                Shares to {signal.action === 'BUY' ? 'buy' : 'sell'}
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="number"
                  min={1}
                  value={qty}
                  onChange={e => onQtyChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  style={{
                    width: '100%', padding: '8px 12px', background: '#0f172a',
                    border: `1px solid ${overMax || overBp ? '#dc2626' : '#374151'}`,
                    borderRadius: 6, color: '#f9fafb', fontSize: 15, fontWeight: 600,
                  }}
                />
                <button
                  onClick={() => onQtyChange(preview.suggestedQty)}
                  style={{ padding: '8px 12px', background: '#374151', color: '#9ca3af', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Trade value preview */}
            <div style={{
              background: '#0f172a', borderRadius: 8, padding: '10px 14px', marginBottom: 16,
              border: `1px solid ${overMax || overBp ? '#7f1d1d' : '#1e3a5f'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: 13 }}>Trade value</span>
                <span style={{ color: '#f9fafb', fontWeight: 700, fontSize: 15 }}>
                  ${tradeValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={{ color: '#6b7280', fontSize: 12 }}>% of equity</span>
                <span style={{ color: pctOfEquity > 20 ? '#f87171' : pctOfEquity > 10 ? '#fbbf24' : '#4ade80', fontSize: 12, fontWeight: 600 }}>
                  {pctOfEquity.toFixed(1)}%
                </span>
              </div>
            </div>

            {overMax && (
              <div style={{ color: '#fbbf24', fontSize: 12, marginBottom: 12 }}>
                Warning: qty exceeds 10% single-trade cap ({preview.maxQty} shares max)
              </div>
            )}
            {overBp && (
              <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>
                Insufficient buying power for this order
              </div>
            )}
            {pctOfEquity > 20 && !overBp && (
              <div style={{ color: '#fbbf24', fontSize: 12, marginBottom: 12 }}>
                Note: this order exceeds 20% of equity — consider reducing qty
              </div>
            )}
          </>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', background: '#374151', color: '#9ca3af', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          {preview && !loading && (
            <button
              onClick={onConfirm}
              disabled={confirming || overBp}
              style={{
                padding: '8px 20px',
                background: overBp ? '#374151' : (signal.action === 'BUY' ? '#16a34a' : '#dc2626'),
                color: overBp ? '#6b7280' : '#fff',
                border: 'none', borderRadius: 6,
                cursor: confirming || overBp ? 'not-allowed' : 'pointer',
                fontWeight: 700, fontSize: 13,
              }}
            >
              {confirming ? 'Placing Order...' : `Execute ${signal.action} ${qty} shares`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Signal Card ──────────────────────────────────────────────────────────────

const SignalCard: React.FC<{
  signal: AlgoSignal;
  onExecute: (signal: AlgoSignal) => void;
  onDismiss: (signalId: string) => void;
  executing: boolean;
}> = ({ signal, onExecute, onDismiss, executing }) => {
  const strategy = STRATEGIES[signal.strategyId];
  const risk = RISK_LABELS[signal.riskAppetite];
  const scoreColor = signal.score >= 80 ? '#16a34a' : signal.score >= 65 ? '#d97706' : '#dc2626';

  return (
    <div style={{
      background: '#1f2937', borderRadius: 10, padding: '18px 22px', marginBottom: 14,
      border: signal.status === 'pending' ? '1px solid #374151' : '1px solid #1f2937',
      opacity: signal.status === 'dismissed' ? 0.5 : 1,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 20, fontWeight: 800, color: '#f9fafb' }}>{signal.symbol}</span>
          <span style={{
            background: (signal.direction === 'short') ? '#dc2626' : '#16a34a',
            color: '#fff', borderRadius: 4, padding: '2px 10px', fontSize: 13, fontWeight: 700,
          }}>
            {(signal.direction === 'short') ? 'SHORT' : 'LONG'}
          </span>
          <span style={{ background: ACTION_COLORS[signal.action], color: '#fff', borderRadius: 4,
            padding: '2px 10px', fontSize: 13, fontWeight: 700 }}>
            {signal.action}
          </span>
          <span style={{ background: scoreColor + '20', color: scoreColor, borderRadius: 4,
            padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
            Policy {signal.score}
          </span>
          {signal.claudeAnalysis && (
            <span style={{ background: (signal.claudeAnalysis.confidence >= 80 ? '#16a34a' : signal.claudeAnalysis.confidence >= 60 ? '#d97706' : '#dc2626') + '20',
              color: signal.claudeAnalysis.confidence >= 80 ? '#16a34a' : signal.claudeAnalysis.confidence >= 60 ? '#d97706' : '#dc2626',
              borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
              AI {signal.claudeAnalysis.confidence}
            </span>
          )}
          <span style={{ background: STATUS_COLORS[signal.status] + '20', color: STATUS_COLORS[signal.status],
            borderRadius: 4, padding: '2px 8px', fontSize: 11 }}>
            {signal.status}
          </span>
        </div>
        <span style={{ color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap', marginLeft: 8 }}>
          {fmtDate(signal.createdAt)}
        </span>
      </div>

      {/* Price progress (for pending signals with known current price) */}
      {signal.status === 'pending' && signal.currentPrice != null && signal.suggestedEntry != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ color: '#6b7280', fontSize: 12 }}>Current price:</span>
          <span style={{ color: '#f9fafb', fontWeight: 700, fontSize: 14 }}>${fmt(signal.currentPrice)}</span>
          {(() => {
            const chg = ((signal.currentPrice - signal.suggestedEntry) / signal.suggestedEntry) * 100;
            const color = chg >= 0 ? '#4ade80' : '#f87171';
            return (
              <span style={{ color, fontSize: 13, fontWeight: 600 }}>
                {chg >= 0 ? '+' : ''}{chg.toFixed(2)}% vs entry
              </span>
            );
          })()}
          {signal.lastCheckedAt && (
            <span style={{ color: '#4b5563', fontSize: 11 }}>as of {fmtDate(signal.lastCheckedAt)}</span>
          )}
        </div>
      )}

      {/* Strategy + risk */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ color: '#a78bfa', fontSize: 12 }}>{strategy?.icon} {strategy?.name}</span>
        <span style={{ background: risk.color + '20', color: risk.color, borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>
          {risk.label}
        </span>
      </div>

      {/* Price levels */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: '#6b7280', fontSize: 11 }}>Entry</div>
          <div style={{ color: '#f9fafb', fontSize: 14, fontWeight: 600 }}>${fmt(signal.suggestedEntry)}</div>
        </div>
        <div>
          <div style={{ color: '#6b7280', fontSize: 11 }}>Stop</div>
          <div style={{ color: '#f87171', fontSize: 14, fontWeight: 600 }}>${fmt(signal.suggestedStop)}</div>
        </div>
        <div>
          <div style={{ color: '#6b7280', fontSize: 11 }}>Target</div>
          <div style={{ color: '#4ade80', fontSize: 14, fontWeight: 600 }}>${fmt(signal.suggestedTarget)}</div>
        </div>
        {signal.suggestedStop && signal.suggestedTarget && signal.suggestedEntry && (
          <div>
            <div style={{ color: '#6b7280', fontSize: 11 }}>R:R</div>
            <div style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 600 }}>
              {fmt((signal.suggestedTarget - signal.suggestedEntry) / (signal.suggestedEntry - signal.suggestedStop), 1)}:1
            </div>
          </div>
        )}
      </div>

      {/* Indicators */}
      {signal.indicators && (
        <div style={{ background: '#111827', borderRadius: 6, padding: '8px 12px', marginBottom: 12,
          display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
          {signal.indicators.rsi !== null && (
            <span style={{ color: '#9ca3af' }}>RSI: <strong style={{ color: '#e5e7eb' }}>{fmt(signal.indicators.rsi, 1)}</strong></span>
          )}
          <span style={{ color: '#9ca3af' }}>Vol: <strong style={{ color: '#e5e7eb' }}>{fmt(signal.indicators.volumeRatio, 2)}×</strong></span>
          <span style={{ color: '#9ca3af' }}>BB: <strong style={{ color: '#e5e7eb' }}>{fmt(signal.indicators.bbPosition, 2)}</strong></span>
          {signal.indicators.macdHistogram !== null && (
            <span style={{ color: '#9ca3af' }}>MACD Hist: <strong style={{ color: signal.indicators.macdHistogram! > 0 ? '#4ade80' : '#f87171' }}>
              {fmt(signal.indicators.macdHistogram, 4)}
            </strong></span>
          )}
          {signal.indicators.atr !== null && (
            <span style={{ color: '#9ca3af' }}>ATR: <strong style={{ color: '#e5e7eb' }}>${fmt(signal.indicators.atr, 2)}</strong></span>
          )}
          {signal.indicators.avwap !== null && (
            <span style={{ color: '#9ca3af' }}>AVWAP: <strong style={{ color: signal.indicators.avwapDistance! >= 0 ? '#4ade80' : '#f87171' }}>
              {signal.indicators.avwapDistance! >= 0 ? '+' : ''}{fmt(signal.indicators.avwapDistance, 1)}%
            </strong></span>
          )}
        </div>
      )}

      {/* Sentiment */}
      {signal.sentiment && <SentimentBadge sentiment={signal.sentiment} />}

      {/* Claude AI Analysis */}
      {signal.claudeAnalysis && <ClaudeAnalysisCard analysis={signal.claudeAnalysis} />}

      {/* Auto-executed badge */}
      {signal.autoExecuted && (
        <div style={{ background: '#064e3b', borderRadius: 6, padding: '6px 12px', marginBottom: 12, fontSize: 12, color: '#6ee7b7', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700 }}>Auto-Executed</span>
          {signal.autoExecuteReason && <span style={{ color: '#9ca3af' }}>{signal.autoExecuteReason}</span>}
        </div>
      )}

      {/* Rationale */}
      {signal.rationale && (
        <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 14, lineHeight: 1.6,
          whiteSpace: 'pre-line', borderLeft: '2px solid #374151', paddingLeft: 10 }}>
          {signal.rationale}
        </div>
      )}

      {/* Actions */}
      {signal.status === 'pending' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => onExecute(signal)}
            disabled={executing}
            style={{ padding: '7px 18px', background: ACTION_COLORS[signal.action],
              color: '#fff', border: 'none', borderRadius: 6, cursor: executing ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 13 }}
          >
            {executing ? 'Opening...' : `Execute ${signal.action}`}
          </button>
          <button
            onClick={() => onDismiss(signal.signalId)}
            style={{ padding: '7px 14px', background: '#374151', color: '#9ca3af',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Submitted order info — order at Alpaca, awaiting fill */}
      {signal.status === 'submitted' && (
        <div style={{ background: '#422006', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#fbbf24' }}>
          Order submitted — awaiting fill at Alpaca. Will reconcile to "filled" automatically on the next engine run.
          {signal.trade?.alpacaOrderId && <> · Order ID: {signal.trade.alpacaOrderId}</>}
        </div>
      )}

      {/* Executed (filled) trade info */}
      {signal.status === 'executed' && signal.trade && (
        <div style={{ background: '#0c2340', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#93c5fd' }}>
          {signal.trade.filledPrice != null
            ? <>Filled: {signal.trade.qty} shares @ ${fmt(signal.trade.filledPrice)} · Order ID: {signal.trade.alpacaOrderId}</>
            : <>Order placed: {signal.trade.qty} shares · awaiting fill price · Order ID: {signal.trade.alpacaOrderId}</>}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

type FilterStatus = 'all' | SignalStatus;

export const AlgoSignals: React.FC = () => {
  const { user, token } = useAuth();
  const [signals, setSignals] = useState<AlgoSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [modal, setModal] = useState<ModalState | null>(null);
  const [confirming, setConfirming] = useState(false);

  const fetchSignals = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const opts = filterStatus !== 'all' ? { status: filterStatus as SignalStatus } : undefined;
      const result = await algoService.getSignals(token, { ...opts, limit: 100 });
      setSignals(result.signals);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load signals');
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus]);

  useEffect(() => { fetchSignals(); }, [fetchSignals]);

  const handleExecuteClick = async (signal: AlgoSignal) => {
    if (!token) return;
    setModal({ signal, loading: true, error: null, preview: null, qty: 1 });
    try {
      const preview = await algoService.previewTrade(token, signal.signalId);
      const initialQty = preview.policyDefaultQty || preview.suggestedQty;
      setModal({ signal, loading: false, error: null, preview, qty: initialQty });
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to load account info';
      setModal(prev => prev ? { ...prev, loading: false, error: msg } : null);
    }
  };

  const handleExecuteConfirm = async () => {
    if (!token || !modal) return;
    setConfirming(true);
    try {
      const result = await algoService.executeTrade(token, modal.signal.signalId, modal.qty);
      // Reflect the truthful state from the backend: order is 'submitted'
      // until Alpaca reports 'filled' — algoEngine.checkSubmittedTrades will
      // promote it on the next engine run, or you can hit Refresh.
      const newStatus = (result.trade?.status as any) || 'submitted';
      setSignals(prev => prev.map(s =>
        s.signalId === modal.signal.signalId ? { ...s, status: newStatus, trade: result.trade as any } : s
      ));
      setModal(null);
    } catch (err: any) {
      const data = err.response?.data || {};
      const parts: string[] = [];
      if (data.error) parts.push(data.error);
      if (Array.isArray(data.violations) && data.violations.length > 0) {
        parts.push('Rule violations:\n  - ' + data.violations.join('\n  - '));
      }
      if (data.alpacaError && !data.error?.includes(String(data.alpacaError).slice(0, 30))) {
        parts.push(`Alpaca: ${data.alpacaError}`);
      }
      if (data.marketNote) parts.push(data.marketNote);
      const msg = parts.length > 0 ? parts.join('\n\n') : 'Failed to execute trade. Try again or check the engine settings.';
      setModal(prev => prev ? { ...prev, error: msg } : null);
    } finally {
      setConfirming(false);
    }
  };

  const handleDismiss = async (signalId: string) => {
    if (!token) return;
    try {
      await algoService.dismissSignal(token, signalId);
      setSignals(prev => prev.map(s => s.signalId === signalId ? { ...s, status: 'dismissed' } : s));
    } catch {
      // Dismiss silently — optimistic UI even if API fails
      setSignals(prev => prev.map(s => s.signalId === signalId ? { ...s, status: 'dismissed' } : s));
    }
  };

  const algoEnabled = user?.algoTradingEnabled;
  const pendingCount = signals.filter(s => s.status === 'pending').length;

  return (
    <div className="dashboard-container">
      <Navigation />

      {/* Execute modal */}
      {modal && (
        <ExecuteModal
          state={modal}
          onQtyChange={qty => setModal(prev => prev ? { ...prev, qty } : null)}
          onConfirm={handleExecuteConfirm}
          onClose={() => setModal(null)}
          confirming={confirming}
        />
      )}

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700, margin: 0 }}>
              Algo Signals <span className="beta-badge" style={{ fontSize: 11, marginLeft: 8 }}>BETA</span>
            </h1>
            <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>
              Strategy signals from your engines. Review and execute with one click.
            </p>
          </div>

          {!algoEnabled && (
            <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📡</div>
              <h2 style={{ color: '#f9fafb' }}>Algo Trading is a Pro Feature</h2>
              <p style={{ color: '#9ca3af' }}>Create an engine to start generating signals automatically.</p>
            </div>
          )}

          {algoEnabled && (
            <>
              {/* Filter bar */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                {(['all', 'pending', 'submitted', 'executed', 'dismissed'] as FilterStatus[]).map(s => (
                  <button key={s} onClick={() => setFilterStatus(s)}
                    style={{ padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                      background: filterStatus === s ? '#2563eb' : '#374151',
                      color: filterStatus === s ? '#fff' : '#9ca3af', fontWeight: filterStatus === s ? 600 : 400 }}>
                    {s === 'all' ? `All (${signals.length})` : s === 'pending' ? `Pending (${pendingCount})` : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
                <button onClick={fetchSignals}
                  style={{ padding: '6px 14px', background: '#111827', color: '#9ca3af', border: '1px solid #374151',
                    borderRadius: 6, cursor: 'pointer', fontSize: 13, marginLeft: 'auto' }}>
                  Refresh
                </button>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading signals...</div>
              ) : error ? (
                <div style={{ color: '#f87171', background: '#7f1d1d', borderRadius: 8, padding: 16 }}>{error}</div>
              ) : signals.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>
                  {filterStatus === 'pending' ? 'No pending signals. Your engines will generate signals on their schedule.' : 'No signals found.'}
                </div>
              ) : (
                signals.map(signal => (
                  <SignalCard
                    key={signal.signalId}
                    signal={signal}
                    onExecute={handleExecuteClick}
                    onDismiss={handleDismiss}
                    executing={modal?.signal.signalId === signal.signalId && modal.loading}
                  />
                ))
              )}
            </>
          )}
      </div>
    </div>
  );
};

export default AlgoSignals;
